import type { Localized } from "../i18n";

/**
 * Turo World's non-pose voice lines — draft §8.
 *
 * 🔴 CONTENT-ADDRESSED: ONE ID, ONE LINE, SHARED EVERYWHERE IT IS SAID. The
 * draft's §8 counts *"distinct lines, not per-beat instances"*, and the praise
 * pool is where that pays: six lines cover every pose in the bank rather than
 * one per pose. A per-beat table would ask the studio — or the generator — for
 * the same sentence fourteen times under fourteen names.
 *
 * 🔴 THE PRAISE POOL IS THE ONE THAT CANNOT BE WRITTEN CARELESSLY. §5 requires
 * the praise beat to be **byte-identical whether the child matched the pose or
 * timed out**. So not one of these six may refer to succeeding: *"You got it!"*
 * played after a child who did nothing is worse than silence, and *"Nice try!"*
 * played after a child who nailed it is a demotion. Every line below is warmth
 * about MOVING, which is true either way.
 *
 * ⚠️ ROTATED, NEVER REPEATED WITHIN A SITTING. One line after every pose stops
 * being praise by the third hearing and becomes a noise the child waits out —
 * the same argument `generate-voice-lines.mjs` records for Bridge Patterns.
 *
 * ⚠️ THESE WERE BILINGUAL (ADR-044). ADR-043 gated the Malayalam behind a
 * native-speaker sign-off that never happened — `TW_ML_REVIEWED` stayed an
 * empty set — and the generator refused to synthesise it. The language, the
 * registry and the gate all went together.
 */

/** → public/shared/audio/{lang}/{audioKey}.mp3. Pre-recorded, never TTS (ADR-011). */
export type VoiceKey = string;

/* ─────────────────────────────────────────── Beat 0 · the frame check ── */

const FRAME: Record<VoiceKey, Localized<string>> = {
  tw_frame_1: { en: "Hello! I am Turo." },
  tw_frame_2: {
    en: "Come and stand where I can see you!",
  },
  tw_frame_3: { en: "There you are! Let us play." },
};

/* ───────────────────────────────────── Beat 1 · the greeting ritual ── */

/**
 * 🔴 A RITUAL, NOT AN INPUT — §7. The beat advances on a 2.5 s timer whatever
 * the microphone heard, or whether one exists. ASR on 3–6-year-old speech stalls
 * on children doing everything right, which for this age band is the worst
 * failure available.
 */
const GREET: Record<VoiceKey, Localized<string>> = {
  tw_greet_1: { en: "Good morning!" },
  tw_greet_2: { en: "Now you say it to me!" },
};

/* ───────────────────────────────── Beat 2 · the intro, first run only ── */

const INTRO: Record<VoiceKey, Localized<string>> = {
  tw_intro_1: {
    en: "Today we make our body strong!",
  },
  tw_intro_2: {
    en: "Copy me, and we will move together!",
  },
};

/* ──────────────────────────────────────────────── the praise pool ── */

/**
 * 🔴 NOT ONE OF THESE SIX MAY MENTION GETTING IT RIGHT.
 *
 * They play unchanged after a met hold and after a six-second timeout, and a
 * three-year-old reads tone perfectly. A line that congratulates an achievement
 * becomes a lie in half the cases it is used, and a line that softens becomes a
 * consolation prize in the other half — which is exactly how a game with no fail
 * states teaches a child that they failed.
 */
const PRAISE: Record<VoiceKey, Localized<string>> = {
  tw_praise_1: { en: "Lovely moving!" },
  tw_praise_2: { en: "Look at you go!" },
  tw_praise_3: { en: "So strong!" },
  tw_praise_4: { en: "Wonderful!" },
  tw_praise_5: { en: "We are moving together!" },
  tw_praise_6: { en: "That was fun!" },
};

/* ──────────────────────────────────────────── Beat 8 · the cool-down ── */

const COOLDOWN: Record<VoiceKey, Localized<string>> = {
  tw_cooldown_1: { en: "Let us sit down and rest." },
  tw_cooldown_2: {
    en: "Take a big breath in, and let it go.",
  },
  tw_cooldown_3: { en: "Our body feels good now." },
};

/* ───────────────────────────────────────── Beat 9 · the session end ── */

/**
 * ⚠️ NO STATS, NO SCORE, NO "FOUR OUT OF FIVE" — §5. What a 3–6-year-old gets is
 * a sticker they can see accumulating and Turo waving. Progress that is *seen*,
 * never measured.
 */
const END: Record<VoiceKey, Localized<string>> = {
  tw_end_1: { en: "You get a sticker!" },
  tw_end_2: { en: "Bye bye! See you next time!" },
};

/* ───────────────────────────────────────────────────────── the table ── */

export const TURO_VOICE_LINES: Readonly<Record<VoiceKey, Localized<string>>> = {
  ...FRAME,
  ...GREET,
  ...INTRO,
  ...PRAISE,
  ...COOLDOWN,
  ...END,
};

/**
 * The rotating praise pool, in order.
 *
 * Exported as its own list because the beat runner has to know WHICH keys may
 * rotate — draft §10 validation 6 checks this pool is at least six and at least
 * as large as the session's pose count.
 */
export const TURO_PRAISE_KEYS: readonly VoiceKey[] = Object.keys(PRAISE);

/** Beat 0's lines, in the order they play. */
export const TURO_FRAME_KEYS: readonly VoiceKey[] = Object.keys(FRAME);

/** Beat 1's greeting ritual. */
export const TURO_GREET_KEYS: readonly VoiceKey[] = Object.keys(GREET);

/** Beat 2, first run only. */
export const TURO_INTRO_KEYS: readonly VoiceKey[] = Object.keys(INTRO);

/** Beat 8. */
export const TURO_COOLDOWN_KEYS: readonly VoiceKey[] = Object.keys(COOLDOWN);

/** Beat 9. */
export const TURO_END_KEYS: readonly VoiceKey[] = Object.keys(END);
